import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getSupabase } from "@/lib/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { X, Mail, Lock, Sparkles } from "lucide-react";
import { useCurrentModal, modalActions } from "@/stores/modal-store";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const FEATURE_MAGICLINK = import.meta.env.VITE_FEATURE_MAGICLINK === "true";

async function sendMagicLink(email: string) {
  if (!FEATURE_MAGICLINK) return;
  const supabase = getSupabase();
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      shouldCreateUser: true,
      emailRedirectTo: `${window.location.origin}/auth/callback`,
    },
  });
  if (error) throw error;
}

type Mode = "password" | "magic";

export default function SignInModal() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentModal = useCurrentModal();

  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [mode, setMode] = useState<Mode>("password");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const isOpen = currentModal === "signin";

  useEffect(() => {
    if (user && isOpen) {
      modalActions.close();
      navigate("/dashboard", { replace: true });
    }
  }, [user, isOpen, navigate]);

  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        resetForm();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const resetForm = () => {
    setEmail("");
    setPwd("");
    setErr("");
    setMsg("");
    setBusy(false);
    setSent(false);
  };

  const isEmailValid = (v: string) => /\S+@\S+\.\S+/.test(v);

  const onPasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    setErr("");
    setMsg("");
    if (!isEmailValid(email)) {
      return setErr("Please enter a valid email address.");
    }
    if (!pwd) {
      return setErr("Please enter your password.");
    }

    setBusy(true);
    try {
      const { error } = await getSupabase().auth.signInWithPassword({
        email,
        password: pwd,
      });
      if (error) {
        console.error("[SignInModal] signInWithPassword error:", error);
        throw error;
      }

      toast.success("Welcome back! Signed in successfully."); // ← toast
      setMsg("Signed in successfully. Redirecting…");
    } catch (e: any) {
      console.error("[SignInModal] signInWithPassword catch:", e);
      setErr(e?.message ?? "Sign-in failed. Please check your credentials.");
    } finally {
      setBusy(false);
    }
  };

  const onMagicLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr("");

    if (!isEmailValid(email)) {
      setErr("Please enter a valid email address.");
      return;
    }

    setBusy(true);
    try {
      await sendMagicLink(email);
      setSent(true);
      toast.success("Magic link sent! Check your inbox."); // ← toast
      setMsg("Check your email! We've sent you a magic login link.");
    } catch (err: any) {
      console.error("[sign-in] sendMagicLink error:", err);
      setErr(
        err?.message ?? "Could not send the magic link. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={modalActions.close}>
      <DialogContent className="sm:max-w-md">
        <div className="relative">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-2xl font-bold text-gray-900">
                  Welcome back
                </DialogTitle>
                <DialogDescription className="text-gray-500 text-sm mt-1">
                  Sign in to your account to continue
                </DialogDescription>
              </div>
            </div>

            {FEATURE_MAGICLINK && (
              <div className="mt-6 flex gap-2 bg-gray-50 p-1 rounded-lg">
                <button
                  onClick={() => setMode("password")}
                  className={`flex-1 px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                    mode === "password"
                      ? "bg-white text-gray-900 shadow-sm ring-1 ring-gray-200"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  <Lock className="h-4 w-4" />
                  Password
                </button>
                <button
                  onClick={() => setMode("magic")}
                  className={`flex-1 px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                    mode === "magic"
                      ? "bg-white text-gray-900 shadow-sm ring-1 ring-gray-200"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  <Sparkles className="h-4 w-4" />
                  Magic Link
                </button>
              </div>
            )}
          </div>

          <div className="p-6">
            <form
              onSubmit={
                mode === "password" ? onPasswordLogin : onMagicLinkSubmit
              }
              className="space-y-5"
            >
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="relative group">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 transition-colors group-focus-within:text-blue-500" />
                  <input
                    type="email"
                    placeholder="you@example.com"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              {(mode === "password" || !FEATURE_MAGICLINK) && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={() => modalActions.open("forgot")}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative group">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 transition-colors group-focus-within:text-blue-500" />
                    <input
                      type="password"
                      placeholder="Enter your password"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      autoComplete="current-password"
                      required={mode === "password"}
                    />
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                className="w-full py-3.5 px-4 bg-blue-600 text-white font-semibold rounded-lg shadow-lg hover:bg-blue-700 active:scale-[0.99] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {busy ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {mode === "password" ? "Signing in..." : "Sending link..."}
                  </span>
                ) : mode === "password" || !FEATURE_MAGICLINK ? (
                  "Sign in"
                ) : (
                  "Send magic link"
                )}
              </button>

              {msg && (
                <div
                  className={`p-3 bg-green-50 border border-green-100 rounded-lg transition-all duration-300 ${
                    msg ? "animate-fade-in" : ""
                  }`}
                >
                  <div className="flex items-center gap-2 text-green-700 text-sm">
                    <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse" />
                    {msg}
                  </div>
                </div>
              )}

              {err && (
                <div
                  className={`p-3 bg-red-50 border border-red-100 rounded-lg transition-all duration-300 ${
                    err ? "animate-fade-in" : ""
                  }`}
                >
                  <div className="flex items-center gap-2 text-red-700 text-sm">
                    <div className="h-2 w-2 bg-red-500 rounded-full animate-pulse" />
                    {err}
                  </div>
                </div>
              )}
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Don't have an account?
                </span>
              </div>
            </div>

            <button
              onClick={() => {
                modalActions.close();
                setTimeout(() => navigate("/membership"), 150);
              }}
              className="w-full py-3 px-4 border-2 border-gray-300 hover:border-gray-400 text-gray-800 font-semibold rounded-lg hover:bg-gray-50 active:scale-[0.99] transition-all duration-200 flex items-center justify-center gap-2"
            >
              Create new account
            </button>

            <p className="text-center text-xs text-gray-500 mt-6">
              By continuing, you agree to our{" "}
              <a
                href="/legal/terms"
                className="text-blue-600 hover:text-blue-700 underline transition-colors"
              >
                Terms
              </a>{" "}
              and{" "}
              <a
                href="/legal/privacy"
                className="text-blue-600 hover:text-blue-700 underline transition-colors"
              >
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
